import autocast from 'autocast';
import classNames from 'classnames';
import dot from 'dot-object';
import PropTypes from 'prop-types';
import React, { Suspense, createRef } from 'react';

import ReactResizeDetector from 'react-resize-detector';
import i18n from '../../i18n';
import FiltersModel from '../../models/Filters';
import Group from '../../models/Group';
import User from '../../models/User';
import Utils from '../../utils/utils';

import Alert from './Alert';
import ResultCount from './ResultCount';

import Headband from '../../components/headband/Headband';
import Screen from '../../utils/Screen';
import SortOrder from './filters/SortOrder';

import { Modal } from '@homeexchange/design';
import Tour from '../../utils/Tour';
import InputSearch from './InputSearch';
import {
    Accessibility,
    Amenities,
    CollectionLuxuryFilter,
    ExchangeType,
    FilterSection,
    Guestpoints,
    Location,
    PetAllergies,
    PropertyType,
    Quality,
    ResidenceSize,
    ResidenceType,
    ReverseSearch,
    SurroundingTags,
    TypeOfPlace
} from './filters-ts';

import Analytics from '../../utils/analytics';
import { getActiveFiltersList } from './utils';

const ActiveFilters = React.lazy(() => import('./ActiveFilters'));
const CustomGuestNb = React.lazy(() => import('./filters/CustomGuestNb'));

if ('moment' in window) {
    moment.locale(document.documentElement.lang);
}

const EXCLUDED_FILTERS = ['availability', 'home', 'placeId', 'sort', 'reciprocal', 'lastMinute'];
const EXCLUDED_ACTIVE_FILTERS = ['availability', 'bounds', 'home', 'placeId', 'sort', 'duration'];

/* eslint-disable react/jsx-no-bind */
export class Filters extends React.PureComponent {
    static propTypes = {
        user: PropTypes.instanceOf(User),
        groups: PropTypes.arrayOf(PropTypes.instanceOf(Group)),
        filters: PropTypes.object,
        updateFilters: PropTypes.func,
        amenities: PropTypes.array,
        surrounding: PropTypes.array,
        gpRange: PropTypes.object,
        query: PropTypes.string,
        resultsCount: PropTypes.number,
        toggleMoreFiltersModalDisplay: PropTypes.func,
        displayAlerts: PropTypes.bool,
        onRemoveFilter: PropTypes.func,
        displayMap: PropTypes.bool,
        openMoreFiltersModal: PropTypes.bool
    };

    static defaultProps = {
        groups: []
    };

    constructor(props) {
        super(props);
        this.state = {
            filters: this.stateFiltersFromProps(props),
            calendar: props.filters.calendar,
            originalBoundsFilters: this.props.filters,
            openGuestNb: false,
            height: 0,
            width: 0,
            screenState: {
                isMobile: Screen.isMobile(),
                noDesktop: Screen.noDesktop()
            },
            showCalendarModal: false
        };
        this.updateGuestpoints = this.updateGuestpoints.bind(this);
        this.onFilterUpdate = this.onFilterUpdate.bind(this);
        this.onFilterUpdateAndFetch = this.onFilterUpdateAndFetch.bind(this);
        this.onFilterMultipleUpdate = this.onFilterMultipleUpdate.bind(this);
        this.onFilterObjectUpdate = this.onFilterObjectUpdate.bind(this);
        this.onExchangeTypeUpdate = this.onExchangeTypeUpdate.bind(this);
        this.onExchangeTypeUpdateAndFetch = this.onExchangeTypeUpdateAndFetch.bind(this);
        this.submitFilters = this.submitFilters.bind(this);
        this.resetFilters = this.resetFilters.bind(this);
        this.updateFilters = this.updateFilters.bind(this);
        this.countFilters = this.countFilters.bind(this);
        this.hideTourForever = this.hideTourForever.bind(this);
        this.customSubmitFilters = this.customSubmitFilters.bind(this);
        this.openGuestNb = this.openGuestNb.bind(this);
        this.handleOpenGuestNb = this.handleOpenGuestNb.bind(this);
        this.updateGuestFilters = this.updateGuestFilters.bind(this);
        this.handleOnQuickFilterChange = this.handleOnQuickFilterChange.bind(this);
        this.handleOnMoreFilterChange = this.handleOnMoreFilterChange.bind(this);
        this.updateMarginTopOfSearchActiveFilters = this.updateMarginTopOfSearchActiveFilters.bind(this);
        this.onResize = this.onResize.bind(this);
        this.setShowCalendarModal = this.setShowCalendarModal.bind(this);
        this.updateCalendar = this.updateCalendar.bind(this);
        this.refreshFiltersLayout = this.refreshFiltersLayout.bind(this);
        this.divRef = createRef();
        this.guestNb = createRef();
        this.popupGuestNb = createRef();
        this.radioHome = createRef();
        this.radioFlat = createRef();
        this.radioPrivate = createRef();
        this.extraFilters = createRef();
        this.handleResize = _.debounce(this.handleResize.bind(this), 200);
        this.getResedenceSizeValue = this.getResedenceSizeValue.bind(this);
        this.timeoutRefreshFiltersLayout = null;
        this.delay = 100;
        this.renderFiltersModal = this.renderFiltersModal.bind(this);
        this.toggleOpenMoreFilters = this.toggleOpenMoreFilters.bind(this);
        this.updateBooleanFilter = this.updateBooleanFilter.bind(this);
        this.reformatBooleanFilter = this.reformatBooleanFilter.bind(this);
    }

    stateFiltersFromProps(props) {
        const filters = new FiltersModel(props.filters);
        return filters.flatify('Filters');
    }

    reformatBooleanFilter(filter, value) {
        if (!Array.isArray(value)) {
            return value;
        }

        switch (value.length) {
            case 1:
                if (filter === FiltersModel.IS_PRIMARY.name) {
                    return value[0] === FiltersModel.IS_PRIMARY.primary ? true : false;
                } else if (filter === FiltersModel.IS_PRIVATE_ROOM.name) {
                    return value[0] === FiltersModel.IS_PRIVATE_ROOM.private ? true : false;
                } else {
                    return value[0];
                }

            case 2:
            default:
                return null;
        }
    }

    filtersFromState() {
        const filters = Object.assign({}, this.state.filters);

        if ((filters.is_primary ?? null) !== null) {
            filters.is_primary = this.reformatBooleanFilter(FiltersModel.IS_PRIMARY.name, filters.is_primary);
        }

        if ((filters.home_type ?? null) !== null) {
            filters.home_type = this.reformatBooleanFilter(FiltersModel.HOME_TYPE.name, filters.home_type);
        }

        if ((filters.is_private_room ?? null) !== null) {
            filters.is_private_room = this.reformatBooleanFilter(
                FiltersModel.IS_PRIVATE_ROOM.name,
                filters.is_private_room
            );
        }

        const valideFilters = FiltersModel.fromFlatFilters(filters);
        valideFilters.set(
            'location',
            Object.assign({}, this.props.filters.location, valideFilters.get('location'))
        );
        valideFilters.set('calendar', Object.assign({}, this.state.calendar));
        return valideFilters.toJSON();
    }

    handleOnQuickFilterChange(filter, value) {
        if (!value) {
            this.props.onRemoveFilter(filter, value);
        } else {
            this.props.updateFilters({ ...this.filtersFromState(), [filter]: value });
        }
    }

    handleOnMoreFilterChange(filter, value) {
        this.updateFilters({
            ...this.state.filters,
            [filter]: value === false ? undefined : value
        });
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        let newOriginalBoundsFilters =
            nextProps.filters &&
            nextProps.filters.placeId &&
            this.state.originalBoundsFilters &&
            !_.isEmpty(this.state.originalBoundsFilters) &&
            this.state.originalBoundsFilters.placeId == nextProps.filters.placeId
                ? this.state.originalBoundsFilters
                : nextProps.filters;

        if (
            nextProps.filters &&
            nextProps.filters.placeId &&
            nextProps.filters.location &&
            nextProps.filters.location != this.state.originalBoundsFilters.location &&
            this.state.originalBoundsFilters.placeId == nextProps.filters.placeId
        ) {
            const prevLocation = this.state.originalBoundsFilters.location;
            newOriginalBoundsFilters = Object.assign({}, nextProps.filters, {
                location: prevLocation
            });
        }

        this.setState({
            filters: this.stateFiltersFromProps(nextProps),
            calendar: nextProps.filters.calendar,
            originalBoundsFilters: newOriginalBoundsFilters
        });
    }

    componentDidUpdate(prevProps) {
        const { openMoreFiltersModal } = this.props;
        if (Screen.isMobile() && openMoreFiltersModal !== prevProps.openMoreFiltersModal) {
            this.updateMarginTopOfSearchActiveFilters();
            this.displaySmartBanner();
        }

        if (prevProps.displayMap === true && this.props.displayMap === false) {
            this.updateHeightSearchFilters(openMoreFiltersModal);
            this.updateMarginTopOfSearchActiveFilters();
        }
    }

    displaySmartBanner() {
        const hasSmartbanner = $('html').hasClass('smartbanner-show');
        const isHidden = $('.smartbanner').hasClass('hide-smartbanner');

        if (isHidden) {
            $('.smartbanner').removeClass('hide-smartbanner');
            $('html').addClass('smartbanner-show');
        } else if (hasSmartbanner) {
            $('.smartbanner').addClass('hide-smartbanner');
            $('html').removeClass('smartbanner-show');
        }
    }

    componentDidMount() {
        document.addEventListener('click', this.handleOpenGuestNb);
        this.handleResize();
        this.searchFilters = document.querySelector('.search-filters');
        window.addEventListener('resize', this.handleResize);
        this.removeResizeListener = Screen.addResizeListener((screenState) => {
            this.setState({ screenState });
        });
        this.debouncedRemoveListener = _.debounce(() => {
            this.removeResizeListener();
        }, 200);
        window.addEventListener('beforeunload', this.debouncedRemoveListener);
    }

    updateMarginTopOfSearchActiveFilters() {
        if (this.searchFilter) {
            this.setState({
                height: this.searchFilter.clientHeight
            });
        }
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleOpenGuestNb);
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('beforeunload', this.debouncedRemoveListener);
        if (this.timeoutRefreshFiltersLayout !== null) {
            clearTimeout(this.timeoutRefreshFiltersLayout);
        }
    }

    updateHeightSearchFilters(openMoreFiltersModal = false) {
        if (!this.searchFilters) return;

        const searchActiveFilters = document.querySelector('.search-active-filters');

        if (Screen.noDesktop()) {
            const searchFilterHeight = this.searchFilter?.clientHeight;

            if (searchFilterHeight !== undefined) {
                const newHeight =
                    openMoreFiltersModal || !searchActiveFilters ? `${searchFilterHeight}px` : 'auto';
                this.setParentHeight(newHeight);
            }
        } else {
            this.setParentHeight('');
        }
    }

    setParentHeight(height) {
        this.searchFilters.style.height = height;
    }

    handleResize() {
        this.updateMarginTopOfSearchActiveFilters();
        this.updateHeightSearchFilters(this.props.openMoreFiltersModal);
    }

    onExchangeTypeUpdate(event) {
        this.onExchangeTypeUpdateFromSelect(event, false);
    }

    onExchangeTypeUpdateAndFetch(event) {
        this.onExchangeTypeUpdateFromSelect(event, true);
    }

    onExchangeTypeUpdateFromSelect(event, fetch) {
        // eslint-disable-next-line camelcase
        const calendar = { ...this.state.calendar };

        if (!event.target.checked) {
            calendar.exchange_types = calendar.exchange_types.filter(
                (exchType) => exchType !== event.target.value
            );
        } else {
            const exchangeTypes = calendar.exchange_types ? [...calendar.exchange_types] : [];
            exchangeTypes.push(event.target.value);
            calendar.exchange_types = exchangeTypes;
        }

        const filtersCopy = JSON.parse(
            JSON.stringify({
                ...this.state.filters,
                calendar
            })
        );
        this.setState({
            filters: filtersCopy,
            calendar
        });
        this.updateFilters(filtersCopy, fetch);
    }

    onFilterUpdate(event) {
        this.updateFilterFormInput(event.target);
    }

    onFilterUpdateAndFetch(event) {
        Analytics.trackGTM('SearchResults', {
            event_data: {
                action: 'click',
                area: 'search',
                search_parameter: 'sort_by',
                search_value: event.target.value
            }
        });
        this.updateFilterFormInput(event.target, true);
    }

    onFilterMultipleUpdate(event) {
        this.updateFilterFormCheckboxMultiple(event.target);
    }

    onFilterObjectUpdate(event) {
        this.updateFilterFormCheckboxObject(event.target);
    }

    isObjectChecked(object, value) {
        if (object && dot.pick(object, this.state.filters) == value) {
            return true;
        }
        return false;
    }

    updateBooleanFilter(event) {
        const { checked, value, name } = event.target;
        const filters = Object.assign({}, this.state.filters);
        let currentValue = value;

        if (name === FiltersModel.IS_PRIVATE_ROOM.name) {
            currentValue = autocast(currentValue)
                ? FiltersModel.IS_PRIVATE_ROOM.private
                : FiltersModel.IS_PRIVATE_ROOM.whole;
        } else if (name === FiltersModel.IS_PRIMARY.name) {
            currentValue = autocast(currentValue)
                ? FiltersModel.IS_PRIMARY.primary
                : FiltersModel.IS_PRIMARY.secondary;
        }

        if (checked) {
            Array.isArray(filters[name])
                ? filters[name].push(currentValue)
                : (filters[name] = [currentValue]);
        } else {
            filters[name] = filters[name]?.filter((val) => val !== currentValue);
            if (filters[name]?.length === 0) {
                delete filters[name];
            }
        }
        this.updateFilters(filters, false);
    }

    updateFilterFormInput(input, fetch) {
        let filters = _.clone(this.state.filters);
        // reset filter
        if (input.type === 'checkbox' && !input.checked) {
            delete filters[input.name];
        } else if (input.id === 'exchange_type' && input.value === '') {
            delete filters[input.name];
        }
        // add/update filter
        else {
            const newFilter = {};
            if (input.name === 'home_type') {
                delete filters.home_type;
            }
            newFilter[input.name] = autocast(input.value);
            filters = Object.assign({}, filters, newFilter);
        }
        this.updateFilters(filters, fetch);
    }

    updateFilterFormCheckboxMultiple(input, fetch) {
        let values = this.state.filters[input.name];
        if (input.checked) {
            values = _.union(values, [autocast(input.value)]);
        } else {
            values = _.without(values, autocast(input.value));
        }
        const newValues = {};
        newValues[input.name] = values;
        const filters = Object.assign({}, this.state.filters, newValues);
        this.updateFilters(filters, fetch);
    }

    updateFilterFormCheckboxObject(input, fetch) {
        let values = dot.pick(input.name, this.state.filters);
        const newValues = {};

        if (input.checked) {
            values = autocast(input.value);
            dot.str(input.name, values, newValues);
        } else {
            values = null;
            dot.remove(input.name, this.state.filters);
        }
        const filters = Object.assign({}, this.state.filters, newValues);
        this.updateFilters(filters, fetch);
    }

    updateGuestFilters(adults, children, babies) {
        let { filters } = this.state;

        filters = {
            ...filters,
            home: {
                size: {
                    beds: {
                        adults: adults ? adults : null,
                        children: children ? children : null,
                        babies: babies ? babies : null
                    }
                }
            }
        };

        Utils.closeModalsFullScreen();

        if (Screen.isMobile()) {
            // Apply for iOS
            Utils.applyStyles('.layout-login.search-body', {
                paddingTop: '80px'
            });

            Utils.applyStyles(
                '.search.container-fluid',
                {},
                {
                    selector: '.row',
                    styles: { height: '' }
                }
            );
        }

        this.updateFilters(filters, true);
        this.setState({ openGuestNb: false });
    }

    openGuestNb(open) {
        const openGuestNb = Boolean(open);

        if (openGuestNb) {
            Utils.openModalsFullScreen();
        } else {
            Utils.closeModalsFullScreen();
        }

        if (Screen.isMobile()) {
            // Apply for iOS
            Utils.applyStyles('.layout-login.search-body', {
                paddingTop: openGuestNb ? 0 : '80px'
            });

            Utils.applyStyles(
                '.search.container-fluid',
                {},
                {
                    selector: '.row',
                    styles: { height: openGuestNb ? 'auto' : '' }
                }
            );
        }

        this.setState({
            openGuestNb,
            outSide: false
        });
    }

    handleOpenGuestNb(event) {
        event.stopPropagation();
        if (
            !this.guestNb.current.contains(event.target) &&
            this.state.openGuestNb &&
            !this.popupGuestNb.current.contains(event.target)
        ) {
            this.setState({
                ...(!Screen.isMobile() && { openGuestNb: false }),
                outSide: true
            });
        }
    }

    updateFilters(filters, fetch) {
        this.setState(
            {
                filters
            },
            () => {
                if (fetch) {
                    this.submitFilters();
                }
            }
        );
    }

    resetFilters(e, toggleModalDisplay = true) {
        e.preventDefault();
        const {
            calendar: { date_ranges: dateRanges, flexibility }
        } = this.props.filters;

        const filtersToAdd = {
            calendar: {
                ...(dateRanges && { date_ranges: [...dateRanges] }),
                flexibility
            }
        };

        this.setState(
            {
                filters: {
                    ...filtersToAdd,
                    home: { ...this.props.filters.home }
                },
                calendar: { ...filtersToAdd.calendar }
            },
            () => {
                if (toggleModalDisplay) {
                    this.props.toggleMoreFiltersModalDisplay();
                }
                // Do not submit if there was no active filters
                if (
                    getActiveFiltersList(this.stateFiltersFromProps(this.props), EXCLUDED_ACTIVE_FILTERS)
                        .length > 0
                ) {
                    this.submitFilters();
                }
            }
        );
    }

    submitFilters() {
        this.props.updateFilters(this.filtersFromState());
    }

    customSubmitFilters() {
        this.toggleOpenMoreFilters();
        this.submitFilters();
        Analytics.trackGTM('SearchResults', {
            event_data: {
                action: 'click',
                text: 'submit_filter',
                area: 'search'
            }
        });
    }

    updateCalendar(calendar) {
        // format dates
        this.setState({
            calendar
        });
        const filtersCopy = JSON.parse(JSON.stringify(this.state.filters));
        this.updateFilters(filtersCopy, true);
    }

    updateGuestpoints(values) {
        const from = values[0];
        const to = values[1];
        const { filters } = this.state;
        filters.guestpoints = Object.assign({}, filters.guestpoints);
        if (!_.isNumber(from) || from == this.props.gpRange.min) {
            delete filters.guestpoints.from;
        } else {
            filters.guestpoints.from = from;
        }
        // set to
        if (!_.isNumber(to) || to == this.props.gpRange.max) {
            delete filters.guestpoints.to;
        } else {
            filters.guestpoints.to = to;
        }
        this.updateFilters(Object.assign({}, this.state.filters, filters));
    }

    hideTour() {
        Tour.hideIfStep(0);
    }

    hideTourForever() {
        Tour.hideForeverIfStep(1);
    }

    tourNextStep() {
        Tour.nextStepIfAtStep('search_tour', 0);
    }

    toggleOpenMoreFilters() {
        Analytics.trackGTM('SearchResults', {
            event_data: {
                action: 'click',
                text: 'more_filters',
                area: 'search'
            }
        });
        const { toggleMoreFiltersModalDisplay, openMoreFiltersModal } = this.props;
        toggleMoreFiltersModalDisplay();
        this.updateHeightSearchFilters(!openMoreFiltersModal);
    }

    countFilters(excludedObj) {
        const { filters } = this.state;
        return Object.entries(filters).reduce((acc, [key, value]) => {
            let size;
            if (_.contains(excludedObj, key)) {
                size = 0;
            } else if (value && value.length && key !== 'preferred_destination') {
                if (key === 'home_type') {
                    size = value.length;
                } else {
                    size = value.filter((v) => !_.contains(excludedObj, v)).length;
                }
            } else if (value && !_.isEmpty(value)) {
                if (key === 'calendar') {
                    size = value.exchange_types?.length ?? 0;
                } else {
                    size = 1;
                }
            } else if (_.isBoolean(value) || _.isString(value) || _.isNumber(value)) {
                if ((key === 'lastMinute' || key === 'luxury') && !value) {
                    size = 0;
                } else {
                    size = 1;
                }
            } else {
                size = 0;
            }
            return acc + size;
        }, 0);
    }

    setShowCalendarModal(show) {
        if (show) {
            Utils.openModalsFullScreen();
        } else {
            Utils.closeModalsFullScreen();
        }

        if (Screen.isMobile()) {
            // Apply for iOS
            Utils.applyStyles('.badge-more-filter', {
                display: show ? 'none' : ''
            });

            Utils.applyStyles('.react-resize-detector', {
                position: show ? 'fixed' : '',
                zIndex: show ? 10 : '',
                top: show ? 0 : '',
                bottom: show ? 0 : '',
                right: show ? 0 : '',
                left: show ? 0 : ''
            });

            // Apply for iOS when input search is feed
            Utils.applyStyles(
                '.search.container-fluid',
                {
                    position: show ? 'fixed' : '',
                    zIndex: show ? 10 : '',
                    top: show ? 0 : '',
                    bottom: show ? 0 : '',
                    right: show ? 0 : '',
                    left: show ? 0 : ''
                },
                {
                    selector: '.row',
                    styles: { height: show ? 'auto' : '' }
                }
            );

            Utils.applyStyles('.search-filters', {
                position: show ? 'fixed' : '',
                zIndex: show ? 10 : ''
            });

            Utils.applyStyles('.form.form-filters', {
                position: show ? 'fixed' : ''
            });

            Utils.applyStyles('.search-filter-container', {
                top: show ? 0 : ''
            });

            Utils.applyStyles('.container-search-filter-row', {
                position: show ? 'fixed' : '',
                zIndex: show ? 10 : '',
                top: show ? 0 : '',
                bottom: show ? 0 : '',
                right: show ? 0 : '',
                left: show ? 0 : ''
            });

            Utils.applyStyles('.search-filter-row', {
                width: show ? '100%' : '',
                overflow: show ? 'initial' : ''
            });
        }

        this.setState({ showCalendarModal: show });
    }

    reactResizeDetector(width, height, targetRef, calendarTree) {
        return (
            <div ref={targetRef} className="react-resize-detector">
                {this.props.user && (
                    <>
                        <div className="form-group date-range visible-xs visible-sm">
                            <label htmlFor="date_range" className="sr-only">
                                {i18n.t('search:filters.arrival')} {'->'} {i18n.t('search:filters.departure')}
                            </label>
                            <InputSearch
                                mode="range"
                                nbMonths={Screen.isMobile() ? 1 : 2}
                                classname={['btn btn-default date_range']}
                                calendar={calendarTree}
                                setCalendar={this.updateCalendar}
                                isFilter={true}
                                showModal={this.state.showCalendarModal}
                                setShowModal={this.setShowCalendarModal}
                                context={'filters'}
                            />
                        </div>
                    </>
                )}
            </div>
        );
    }

    onResize(width) {
        this.setState({ width });
    }

    refreshFiltersLayout() {
        this.timeoutRefreshFiltersLayout = setTimeout(() => {
            this.updateHeightSearchFilters();
            this.updateMarginTopOfSearchActiveFilters();
        }, this.delay);
    }

    handleBecomeMemberBannerClick(e) {
        e.preventDefault();
        Analytics.trackGTM('SearchResults', {
            event_data: {
                action: 'click',
                text: 'become_member_search-banner',
                area: 'search'
            }
        });

        window.open(e.target.href, '_blank');
    }

    renderDesktopFiltersButton(nbFilters) {
        return (
            <div
                className="form-group more-filters"
                onFocus={this.hideTourForever}
                onClick={this.hideTourForever}
            >
                <button
                    onClick={this.toggleOpenMoreFilters}
                    type="button"
                    className={'btn btn-default custom-filter'}
                    role="button"
                >
                    <i className="icon icon-more-filter icon-large" />
                    {i18n.t('search:filters-button')}
                    {nbFilters > 0 && <span className="badge badge-more-filter">{nbFilters}</span>}
                </button>
            </div>
        );
    }

    renderMobileFiltersButton(nbFilters) {
        return (
            <div
                className="form-group more-filters"
                onFocus={this.hideTourForever}
                onClick={this.hideTourForever}
            >
                <div onClick={this.toggleOpenMoreFilters} className="icon-badge-more-filter">
                    <i className="icon icon-more-filter icon-large" />
                    {nbFilters > 0 && <span className="badge badge-more-filter">{nbFilters}</span>}
                </div>
            </div>
        );
    }

    renderFiltersModal() {
        const { user, toggleMoreFiltersModalDisplay, openMoreFiltersModal } = this.props;
        return (
            <Modal
                title={i18n.t('common:filters')}
                confirmActionText={i18n.t('search:filter.submit')}
                onConfirmAction={this.customSubmitFilters}
                alternativeAction={i18n.t('search:reset_filters')}
                onAlternativeAction={(e) => this.resetFilters(e, false)}
                isOpen={openMoreFiltersModal}
                onClose={toggleMoreFiltersModalDisplay}
            >
                {(user?.get('is_he_collection') || user?.isUserEligibleCollection()) && (
                    <FilterSection title={i18n.t('search:collection_homes_filter')}>
                        <CollectionLuxuryFilter
                            onChange={this.handleOnMoreFilterChange}
                            checked={this.state.filters.luxury ?? false}
                            isEligibleCollection={user?.isUserEligibleCollection()}
                        />
                    </FilterSection>
                )}
                <FilterSection title={i18n.t('search:filter.exchange_type.title')}>
                    <ExchangeType
                        exchangeType={this.state.filters.calendar.exchange_types || []}
                        onChange={this.onExchangeTypeUpdate}
                    />
                </FilterSection>
                {this.props.user?.get('homes')?.some((h) => !h.isOffline()) && (
                    <FilterSection title={i18n.t('search:filter.reverse-search')}>
                        <ReverseSearch filters={this.state.filters} onChange={this.updateFilters} />
                    </FilterSection>
                )}
                <FilterSection title={i18n.t('search:filter.guestpoints_per_night')}>
                    <Guestpoints
                        gpRange={this.props.gpRange}
                        onChange={this.updateGuestpoints}
                        guestpoints={this.state.filters?.guestpoints || {}}
                    />
                </FilterSection>
                <FilterSection title={i18n.t('search:filter.home-type.title')}>
                    <PropertyType
                        homeType={this.state.filters.home_type}
                        onChange={this.updateBooleanFilter}
                    />
                </FilterSection>
                <FilterSection title={i18n.t('search:filter.type-of-place.title')}>
                    <TypeOfPlace
                        isPrivate={this.state.filters.is_private_room}
                        onChange={this.updateBooleanFilter}
                    />
                </FilterSection>
                <FilterSection title={i18n.t('search:filter.residence-type')}>
                    <ResidenceType
                        inputValue={this.state.filters.is_primary ?? null}
                        onChange={this.updateBooleanFilter}
                    />
                </FilterSection>
                <FilterSection title={i18n.t('search:filter.size')}>
                    <ResidenceSize
                        bedroomValue={this.state.filters?.bedrooms}
                        bathroomValue={this.state.filters?.bathrooms}
                        putUpBedValue={this.getResedenceSizeValue('no-bed-up')}
                        bedroomChangeHandler={this.onFilterUpdate}
                        bathroomChangeHandler={this.onFilterUpdate}
                        putUpBedChangeHandler={this.onFilterMultipleUpdate}
                    />
                </FilterSection>
                <FilterSection title={i18n.t('home:location')}>
                    <Location
                        values={this.state.filters?.amenities || []}
                        onChange={this.onFilterMultipleUpdate}
                    />
                </FilterSection>
                <FilterSection title={i18n.t('search:filter.surrounding-tags')}>
                    <SurroundingTags
                        surrounding={this.state.filters?.surrounding}
                        onUpdate={this.onFilterMultipleUpdate}
                    />
                </FilterSection>
                <FilterSection title={i18n.t('search:filter.amenities')}>
                    <Amenities
                        filters={this.state.filters}
                        onFilterMultipleUpdate={this.onFilterMultipleUpdate}
                        onFilterUpdate={this.onFilterUpdate}
                    />
                </FilterSection>
                <FilterSection title={i18n.t('search:filter.accessibility')}>
                    <Accessibility
                        values={this.state.filters?.amenities}
                        onUpdate={this.onFilterMultipleUpdate}
                    />
                </FilterSection>
                <FilterSection title={i18n.t('search:filter.pet_allergics')}>
                    <PetAllergies
                        values={this.state.filters?.exclude_animals}
                        onUpdate={this.onFilterMultipleUpdate}
                    />
                </FilterSection>
                <FilterSection title={i18n.t('search:filter.quality')}>
                    <Quality
                        values={this.state.filters.filters ?? []}
                        onUpdate={this.onFilterMultipleUpdate}
                    />
                </FilterSection>
            </Modal>
        );
    }

    getResedenceSizeValue(name) {
        return _.contains(this.state.filters.filters, name);
    }

    render() {
        const { user } = this.props;
        let excludeFilters = [...EXCLUDED_FILTERS];

        if (Screen.isMobile()) {
            excludeFilters = excludeFilters.filter(
                (filter) =>
                    filter !== 'preferred_destination' &&
                    filter !== 'reciprocal' &&
                    filter !== 'lastMinute' &&
                    filter !== 'luxury'
            );
        }

        if (user && user.has('is_he_collection') && user.get('is_he_collection')) {
            excludeFilters = excludeFilters.filter((filter) => filter !== 'lastMinute');
        }

        const nbFilters = this.state.filters ? this.countFilters(excludeFilters) : 0;

        const calendarTree = this.state.calendar ? JSON.parse(JSON.stringify(this.state?.calendar)) : null;

        return (
            <div
                className={classNames('search-filters col-xs-12', {
                    'search-filters-become-member':
                        !user?.get('is_he_collection') && !user?.get('subscription')
                })}
            >
                <form className="form form-filters">
                    <div
                        id="search-filter"
                        className={classNames('search-filter-container', {
                            'search-become-member':
                                !user?.get('is_he_collection') && !user?.get('subscription')
                        })}
                        ref={(searchFilter) => {
                            this.searchFilter = searchFilter;
                        }}
                    >
                        <div
                            className="headband-filter"
                            style={{ display: Screen.noDesktop() ? 'block' : 'none' }}
                        >
                            <Headband refreshFiltersLayout={this.refreshFiltersLayout} />
                        </div>
                        {!user?.get('is_he_collection') && !user?.get('subscription') && (
                            <div className="headband-become-member">
                                <div className="container-become-member col-xs-12">
                                    <div className="container-gift">
                                        <i className="icon-gift_blue" />
                                        <p
                                            dangerouslySetInnerHTML={{
                                                __html: i18n.t('search:become_member')
                                            }}
                                        />
                                    </div>
                                    <div className="container-link-payment">
                                        <a
                                            href={i18n.t('url:subscription_payment')}
                                            onClick={this.handleBecomeMemberBannerClick}
                                        >
                                            {i18n.t('subscription:subscription_from_search')}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="container-search-filter-row">
                            <div className="search-filter-row">
                                <ReactResizeDetector handleWidth onResize={this.onResize}>
                                    {({ width, height, targetRef }) =>
                                        this.reactResizeDetector(width, height, targetRef, calendarTree)
                                    }
                                </ReactResizeDetector>

                                <div className="form-group hidden-md hidden-lg" ref={this.divRef}>
                                    <Suspense fallback={<div>Loading</div>}>
                                        <CustomGuestNb
                                            customGuestNbRef={this.guestNb}
                                            popupRef={this.popupGuestNb}
                                            divRef={this.divRef}
                                            onChange={this.updateGuestFilters}
                                            open={this.state.openGuestNb}
                                            openGuestNb={this.openGuestNb}
                                            outSide={this.state.outSide}
                                            home={this.state.filters.home}
                                            widthSearchFilterRow={this.state.width}
                                            portal
                                            showTitle={true}
                                        />
                                    </Suspense>
                                </div>

                                {this.props.resultsCount > 0 && Screen.noDesktop() && (
                                    <>
                                        <SortOrder onChange={this.onFilterUpdateAndFetch} />
                                        <div className="form-group save-search">
                                            <Alert
                                                user={this.props.user}
                                                search={{
                                                    query: this.props.query,
                                                    filters: this.state.originalBoundsFilters
                                                }}
                                                withTooltip={false}
                                            />
                                        </div>
                                    </>
                                )}

                                {Screen.isTabletOrUpper() && (
                                    <div className="form-group desktop-row">
                                        {this.renderDesktopFiltersButton(nbFilters)}
                                        {this.renderFiltersModal()}
                                        {this.props.resultsCount > 0 && (
                                            <div className="sort-and-save">
                                                <SortOrder onChange={this.onFilterUpdateAndFetch} />
                                                {this.props.displayAlerts && (
                                                    <div className="form-group save-search">
                                                        <Alert
                                                            user={this.props.user}
                                                            search={{
                                                                query: this.props.query,
                                                                filters: this.state.originalBoundsFilters
                                                            }}
                                                            withTooltip={true}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {Screen.noDesktop() && (
                                <>
                                    {this.renderMobileFiltersButton(nbFilters)}
                                    {this.renderFiltersModal()}
                                </>
                            )}
                        </div>
                    </div>
                    {nbFilters > 0 && (
                        <Suspense fallback={<div>Loading</div>}>
                            <ActiveFilters
                                style={
                                    Screen.noDesktop() &&
                                    this.state.height > FiltersModel.HEIGHT_SEARCH_FILTER_ROW
                                        ? { marginTop: this.state.height }
                                        : {}
                                }
                                filters={this.state.filters}
                                excludes={['availability', 'bounds', 'home', 'placeId', 'sort', 'duration']}
                                query={this.state.query}
                                removeFilter={this.props.onRemoveFilter}
                                showFilters={this.openMoreFilters}
                                resetFilters={(e) => this.resetFilters(e, false)}
                                chunk
                                refreshFiltersLayout={this.refreshFiltersLayout}
                            />
                        </Suspense>
                    )}
                    {this.props.resultsCount > 0 && Screen.isTabletOrUpper() && (
                        <div className="home-listing-title">
                            <div className="form-group results-count hidden-xs">
                                {this.props.resultsCount < 10000 && this.props.query ? (
                                    i18n.t('search:nb_result_home', { count: this.props.resultsCount })
                                ) : (
                                    <ResultCount
                                        count={this.props.resultsCount}
                                        classes="hidden-xs hidden-sm"
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </div>
        );
    }
}

export default Filters;
